import React, { useState, useEffect } from "react";
import { Row, Col, Spin, Result, Button } from "antd";
import { useLocation, Link } from "react-router-dom";
import { verifyAccountAction } from "@api/account/accountAction";
import { FiAlertTriangle, FiTrash2, FiPause, FiCheck, FiX, FiRefreshCw } from "react-icons/fi";
import logoText from "@assets/images/logo/dark-logo-text-nobg.png";

const VerifyAccountAction = () => {
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [message, setMessage] = useState("");

  const search = useLocation().search;
  const token = new URLSearchParams(search).get("token");

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError("Invalid verification link. No token provided.");
      return;
    }

    verifyAccountAction(token)
      .then((res) => {
        setLoading(false);
        if (res.data.success) {
          setSuccess(true);
          setActionType(res.data.action);
          setMessage(res.data.message);
        } else {
          setError(res.data.error || "Verification failed");
        }
      })
      .catch((err) => {
        setLoading(false);
        setError(
          err.response?.data?.error ||
            "An error occurred during verification. Please try again."
        );
      });
  }, [token]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f8fafc",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <Spin size="large" />
          <p style={{ marginTop: "16px", color: "#64748b" }}>
            Processing your request...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f8fafc",
        padding: "20px",
      }}
    >
      <div
        style={{
          maxWidth: "480px",
          width: "100%",
          background: "#fff",
          borderRadius: "16px",
          padding: "40px",
          boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
          textAlign: "center",
        }}
      >
        <img
          src={logoText}
          alt="Copilotly"
          style={{
            maxWidth: "160px",
            height: "auto",
            marginBottom: "24px",
          }}
        />

        {success ? (
          <>
            <div
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                background:
                  actionType === "delete"
                    ? "linear-gradient(135deg, #ef4444, #dc2626)"
                    : actionType === "reactivate"
                    ? "linear-gradient(135deg, #10b981, #059669)"
                    : "linear-gradient(135deg, #f59e0b, #d97706)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 24px",
              }}
            >
              {actionType === "delete" ? (
                <FiTrash2 size={36} color="white" />
              ) : actionType === "reactivate" ? (
                <FiCheck size={36} color="white" />
              ) : (
                <FiPause size={36} color="white" />
              )}
            </div>

            <h2
              style={{
                fontSize: "24px",
                fontWeight: 600,
                color: actionType === "delete" ? "#ef4444" : actionType === "reactivate" ? "#059669" : "#f59e0b",
                marginBottom: "12px",
              }}
            >
              {actionType === "delete"
                ? "Account Deleted"
                : actionType === "reactivate"
                ? "Account Reactivated"
                : "Account Disabled"}
            </h2>

            <p
              style={{
                fontSize: "14px",
                color: "#64748b",
                marginBottom: "24px",
                lineHeight: 1.6,
              }}
            >
              {message}
            </p>

            {actionType === "disable" && (
              <div
                style={{
                  padding: "16px",
                  background: "#fffbeb",
                  borderRadius: "8px",
                  marginBottom: "24px",
                  border: "1px solid #fef3c7",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    color: "#92400e",
                  }}
                >
                  To reactivate your account, please contact{" "}
                  <a
                    href="mailto:support@copilotly.com"
                    style={{ color: "#d97706", fontWeight: 500 }}
                  >
                    support@copilotly.com
                  </a>
                </p>
              </div>
            )}

            {actionType === "delete" && (
              <div
                style={{
                  padding: "16px",
                  background: "#fef2f2",
                  borderRadius: "8px",
                  marginBottom: "24px",
                  border: "1px solid #fecaca",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    color: "#dc2626",
                  }}
                >
                  All your data has been permanently deleted. This action
                  cannot be reversed.
                </p>
              </div>
            )}

            {actionType === "reactivate" && (
              <div
                style={{
                  padding: "16px",
                  background: "#ecfdf5",
                  borderRadius: "8px",
                  marginBottom: "24px",
                  border: "1px solid #a7f3d0",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    color: "#059669",
                  }}
                >
                  Welcome back! Your account is now active. You can sign in and
                  start using Copilotly again.
                </p>
              </div>
            )}

            <Link to={actionType === "reactivate" ? "/login" : "/"}>
              <Button
                type="primary"
                size="large"
                style={{
                  background: actionType === "reactivate" ? "#059669" : "#3b82f6",
                  border: "none",
                  height: "44px",
                  borderRadius: "8px",
                  fontWeight: 500,
                }}
              >
                {actionType === "reactivate" ? "Sign In" : "Go to Homepage"}
              </Button>
            </Link>
          </>
        ) : (
          <>
            <div
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #ef4444, #dc2626)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 24px",
              }}
            >
              <FiX size={36} color="white" />
            </div>

            <h2
              style={{
                fontSize: "24px",
                fontWeight: 600,
                color: "#ef4444",
                marginBottom: "12px",
              }}
            >
              Verification Failed
            </h2>

            <p
              style={{
                fontSize: "14px",
                color: "#64748b",
                marginBottom: "24px",
                lineHeight: 1.6,
              }}
            >
              {error}
            </p>

            <div
              style={{
                padding: "16px",
                background: "#f8fafc",
                borderRadius: "8px",
                marginBottom: "24px",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "13px",
                  color: "#64748b",
                }}
              >
                If you believe this is an error, please contact{" "}
                <a
                  href="mailto:support@copilotly.com"
                  style={{ color: "#3b82f6", fontWeight: 500 }}
                >
                  support@copilotly.com
                </a>
              </p>
            </div>

            <Link to="/profile/security">
              <Button
                type="primary"
                size="large"
                style={{
                  background: "#3b82f6",
                  border: "none",
                  height: "44px",
                  borderRadius: "8px",
                  fontWeight: 500,
                }}
              >
                Go to Security Settings
              </Button>
            </Link>
          </>
        )}

        <div style={{ marginTop: "24px" }}>
          <Link
            to="/privacy"
            style={{
              fontSize: "12px",
              color: "#94a3b8",
              marginRight: "16px",
            }}
          >
            Privacy Policy
          </Link>
          <Link
            to="/terms"
            style={{
              fontSize: "12px",
              color: "#94a3b8",
            }}
          >
            Terms of Service
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyAccountAction;
